import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Grid,
  Skeleton,
  Alert,
  Button,
  Modal,
  TextField,
  InputLabel,
  Input,
  IconButton,
  InputAdornment,
  Chip,
} from '@mui/material';
import { FaUser, FaEnvelope, FaPhone, FaShieldAlt, FaCloudUploadAlt, FaEye, FaEyeSlash, FaDesktop, FaSignOutAlt } from 'react-icons/fa';
import { postFileUpload, postData, getData, deleteData } from '../../Webservice/Webservice';

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    avatar: null,
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData = sessionStorage.getItem('user');
        
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setFormData({
            firstName: parsedUser.firstName || parsedUser.name || parsedUser.fullName || '',
            lastName: parsedUser.lastName || parsedUser.lName || '',
            email: parsedUser.email || '',
            phoneNumber: parsedUser.phoneNumber || parsedUser.phone || parsedUser.contact || '',
            avatar: parsedUser.avatar || parsedUser.profileImage || parsedUser.image || null,
          });
          setImagePreview(parsedUser.avatar || parsedUser.profileImage || parsedUser.image || null);
        } else {
          setError('No user data found. Please log in again.');
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Error loading user data. Please log in again.');
      } finally {
        setLoading(false);
      }
    };
    loadUserData();
  }, []);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await getData('auth/sessions', null, token, false);
      setSessions(response?.sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleRevokeSession = async (sessionId, isCurrent) => {
    if (isCurrent) {
      const confirmed = window.confirm('This is your current session. Revoking it will log you out. Continue?');
      if (!confirmed) return;
    }
    setRevokingId(sessionId);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await deleteData(`auth/sessions/${sessionId}`, token);
      if (isCurrent) {
        sessionStorage.clear();
        window.location.replace('/');
        return;
      }
      await fetchSessions();
    } catch (err) {
      console.error('Error revoking session:', err);
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAllOtherSessions = async () => {
    const confirmed = window.confirm('This will log out every other device signed into your account. Continue?');
    if (!confirmed) return;
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postData('auth/sessions/revoke-others', {}, token);
      await fetchSessions();
    } catch (err) {
      console.error('Error revoking other sessions:', err);
    }
  };

  const describeDevice = (userAgent) => {
    if (!userAgent) return 'Unknown device';
    if (/mobile/i.test(userAgent)) return 'Mobile browser';
    if (/chrome/i.test(userAgent)) return 'Chrome';
    if (/firefox/i.test(userAgent)) return 'Firefox';
    if (/safari/i.test(userAgent)) return 'Safari';
    if (/edg/i.test(userAgent)) return 'Edge';
    return 'Browser';
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setModalError(null);
    setImagePreview(user.avatar || user.profileImage || user.image || null);
  };

  const handleOpenPasswordModal = () => setOpenPasswordModal(true);
  const handleClosePasswordModal = () => {
    setOpenPasswordModal(false);
    setPasswordError(null);
    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'email' && modalError?.includes('email')) {
      setModalError(null);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordError) {
      setPasswordError(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, avatar: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError(null);

    if (!formData.firstName || formData.firstName.trim().length < 2) {
      setModalError('First Name is required and must be at least 2 characters.');
      return;
    }
    if (!formData.lastName || formData.lastName.trim().length < 2) {
      setModalError('Last Name is required and must be at least 2 characters.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setModalError('Please enter a valid email address.');
      return;
    }

    if (formData.phoneNumber && !/^\d{10}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      setModalError('Phone number must be exactly 10 digits.');
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      const dataToSend = new FormData();
      if (formData.firstName) dataToSend.append('firstName', formData.firstName);
      if (formData.lastName) dataToSend.append('lastName', formData.lastName);
      if (formData.email) dataToSend.append('email', formData.email);
      if (formData.phoneNumber) dataToSend.append('phoneNumber', formData.phoneNumber);
      if (formData.avatar instanceof File) dataToSend.append('image', formData.avatar);

      const response = await postFileUpload('profile/update', dataToSend, token);
      if (response) {
        const updatedUser = {
          ...user,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          avatar: formData.avatar instanceof File ? URL.createObjectURL(formData.avatar) : formData.avatar,
        };
        setUser(updatedUser);
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        window.dispatchEvent(new Event('userUpdate'));
        handleCloseModal();
      } else {
        setModalError(response.message || 'Failed to update user data.');
      }
    } catch (err) {
      console.error('Error updating user data:', err);
      if (err.message.includes('E11000 duplicate key error')) {
        setModalError('This email is already in use. Please choose a different email.');
      } else {
        setModalError('Error updating user data. Please try again.');
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (!passwordData.oldPassword) {
      setPasswordError('Please enter your current password.');
      return;
    }
    if (!passwordData.newPassword || passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      const response = await postData('profile/update/password', passwordData, token);
      if (response) {
        handleClosePasswordModal();
      } else {
        setPasswordError(response.message || 'Failed to update password.');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError('Error updating password. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6 }}>
        <Box
          sx={{
            maxWidth: 900,
            mx: 'auto',
            display: 'flex',
            gap: 4,
            background: '#fff',
            borderRadius: 3,
            boxShadow: 2,
            p: 4,
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#f7fafd',
              borderRadius: 3,
              p: 4,
              minWidth: 260
            }}
          >
            <Skeleton variant="circular" width={180} height={180} />
            <Skeleton variant="text" width={160} height={28} sx={{ mt: 2 }} />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
          <Box sx={{
            flex: 2,
            pl: { md: 6, xs: 0 },
            pt: { xs: 4, md: 0 },
            background: '#f7fafd',
            borderRadius: 3,
          }}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <Box key={idx} sx={{ mb: 1.5 }}>
                <Skeleton variant="rectangular" width="100%" height={40} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 6 }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
          No user data available. Please log in again.
        </Alert>
      </Box>
    );
  }

  const userData = {
    firstName: user.firstName || user.name || user.fullName || 'User',
    lastName: user.lastName || user.lName || 'User',
    email: user.email || 'No email provided',
    role: user.role || user.userType || 'User',
    country: user.country || user.location || 'Not specified',
    avatar: user.avatar || user.profileImage || user.image || 'no image',
    emailVerification: user.emailVerification || user.emailVerified || 'Pending',
    phoneNumber: user.phoneNumber || user.phone || user.contact || 'Not provided',
    status: user.status || (user.isActive ? 'Inactive' : 'Active')
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h3" component="h1" sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          fontFamily: 'Mulish, sans-serif'
        }}>
          User Profile
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleOpenModal}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              textTransform: 'none',
              backgroundColor: 'rgb(255 209 2)',
              color: 'black',
              border: '1px solid black',
              '&:hover': { backgroundColor: 'rgb(255 209 2)' }
            }}
          >
            Edit Profile
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenPasswordModal}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              textTransform: 'none',
              backgroundColor: 'rgb(255 209 2)',
              color: 'black',
              border: '1px solid black',
              '&:hover': { backgroundColor: 'rgb(255 209 2)' }
            }}
          >
            Update Password
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          display: 'flex',
          gap: 4,
          background: '#fff',
          borderRadius: 3,
          boxShadow: 2,
          p: 4,
          flexDirection: { xs: 'column', md: 'row' }
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: '#f7fafd',
            borderRadius: 3,
            p: 4,
            minWidth: 260
          }}
        >
          <Avatar
            src={userData.avatar}
            alt={userData.firstName}
            sx={{ width: 180, height: 180, mb: 2, boxShadow: 1 }}
          />
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1, fontFamily: 'Mulish, sans-serif' }}>
            {userData.firstName} {userData.lastName}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 15, mt: 0.5, fontFamily: 'Mulish, sans-serif' }}>
            {userData.email}
          </Typography>
        </Box>
        <Box sx={{
          flex: 2,
          pl: { md: 6, xs: 0 },
          pt: { xs: 4, md: 0 },
          background: '#f7fafd',
          borderRadius: 3,
        }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <InfoRow icon={<FaUser />} label="First Name" value={userData.firstName} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InfoRow icon={<FaUser />} label="Last Name" value={userData.lastName} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InfoRow icon={<FaShieldAlt />} label="Role" value={userData.role} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InfoRow icon={<FaEnvelope />} label="Email" value={userData.email} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InfoRow icon={<FaPhone />} label="Contact" value={userData.phoneNumber} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InfoRow
                icon={<FaShieldAlt />}
                label="Status"
                value={userData.status}
                valueColor={userData.status === 'Active' ? '#22c55e' : '#f97316'}
                fontWeight={700}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          mt: 4,
          background: '#fff',
          borderRadius: 3,
          boxShadow: 2,
          p: 4,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'Mulish, sans-serif' }}>
            <FaDesktop /> Active Sessions
          </Typography>
          {sessions.length > 1 && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<FaSignOutAlt />}
              onClick={handleRevokeAllOtherSessions}
              sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none' }}
            >
              Log out all other devices
            </Button>
          )}
        </Box>

        {sessionsLoading ? (
          <Skeleton variant="rectangular" width="100%" height={80} />
        ) : sessions.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>
            No active sessions found.
          </Typography>
        ) : (
          sessions.map((s) => (
            <Box
              key={s.id}
              sx={{
                background: '#f6fafd',
                borderRadius: 2,
                px: 3,
                py: 2,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontFamily: 'Mulish, sans-serif' }}>
                    {describeDevice(s.userAgent)}
                  </Typography>
                  {s.current && (
                    <Chip label="This device" size="small" color="success" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                  {s.ip ? `IP: ${s.ip} · ` : ''}Last active: {new Date(s.lastActiveAt).toLocaleString()}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={revokingId === s.id}
                onClick={() => handleRevokeSession(s.id, s.current)}
                sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none' }}
              >
                {revokingId === s.id ? 'Revoking...' : s.current ? 'Log out' : 'Revoke'}
              </Button>
            </Box>
          ))
        )}
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="edit-profile-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="edit-profile-modal" variant="h6" component="h2" sx={{ mb: 3, fontFamily: 'Mulish, sans-serif' }}>
            Edit Profile
          </Typography>
          {modalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {modalError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={imagePreview}
                alt="Profile Preview"
                sx={{ width: 100, height: 100, boxShadow: 1 }}
              />
            </Box>
            <InputLabel htmlFor="avatar-upload" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
              <FaCloudUploadAlt />
              Upload Profile Image
            </InputLabel>
            <Input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              sx={{ display: 'none' }}
            />
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              fullWidth
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  color: 'red',
                  borderColor: 'red',
                  '&:hover': { borderColor: 'red' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  backgroundColor: 'rgb(255 209 2)',
                  color: 'black',
                  border: '1px solid black',
                  '&:hover': { backgroundColor: 'rgb(255 209 2)' }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openPasswordModal}
        onClose={handleClosePasswordModal}
        aria-labelledby="update-password-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="update-password-modal" variant="h6" component="h2" sx={{ mb: 3, fontFamily: 'Mulish, sans-serif' }}>
            Update Password
          </Typography>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Old Password"
              name="oldPassword"
              type={showOldPassword ? 'text' : 'password'}
              value={passwordData.oldPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <FaEye /> :  <FaEyeSlash /> }
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New Password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <FaEye /> :  <FaEyeSlash />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <FaEye /> :  <FaEyeSlash />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClosePasswordModal}
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  color: 'red',
                  borderColor: 'red',
                  '&:hover': { borderColor: 'red' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  backgroundColor: 'rgb(255 209 2)',
                  color: 'black',
                  border: '1px solid black',
                  '&:hover': { backgroundColor: 'rgb(255 209 2)' }
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

function InfoRow({ icon, label, value, valueColor = '#222', fontWeight = 600 }) {
  return (
    <Box
      sx={{
        background: '#f6fafd',
        borderRadius: 2,
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        mb: 1
      }}
    >
      <Box sx={{ mr: 2, color: '#888' }}>
        {icon}
      </Box>
      <Typography sx={{ flex: 1, color: '#888', fontWeight: 500, fontSize: 15 }}>
        {label}
      </Typography>
      <Typography sx={{ mx: 1, color: '#888', fontWeight: 500, fontSize: 15 }}>
        :
      </Typography>
      <Typography
        sx={{
          flex: 2,
          color: valueColor,
          fontWeight,
          fontSize: 15,
          wordBreak: 'break-all'
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default Profile;